import { inject, Injectable } from '@angular/core';

// Nur der Typ - die Bibliothek selbst wird erst beim Export geholt (xlsx-laden.ts).
import type * as XLSX from 'xlsx';
import { ladeXlsx } from './import/xlsx-laden';

import { DataService } from './data.service';
import { AufnahmePunkt, AufnahmePunktDocument, BodenVegetation, BodenvegetationZeile, CURRENT_VERSION, ExtendedHeader, Header, HorizontZeile, LocationSystem, SubstratZeile, bodenformString, dateString, stfgString, neueZeilenKennung } from './data-model';
import { cloneDeep, isEmpty } from 'lodash';
import { altNeuConverter } from './bohrpunkte/filter/application.filter';
import { TransformationService, FileExportService } from '@luzula/luzula-lib';
import { Point } from 'leaflet';
import Hauptbodenformen from '../assets/json/Hauptbodenformen.json';
import { LEGACY_HBF_CODE_MAP } from './hbf-legacy-codes';

/**
 * Eine Horizontzeile, wie der Import sie baut: IMMER mit Tiefe und Maechtigkeit.
 * Im allgemeinen Typ duerfen beide fehlen (eine in der App frisch angelegte Zeile
 * ist leer); beim Import entsteht eine Zeile dagegen nur aus einer Tabellenzeile
 * mit Tiefe. Das Zusammenfuehren am 160-cm-Bruch rechnet mit beiden Werten und
 * nimmt deshalb diesen engeren Typ.
 */
type ImportHorizontZeile = HorizontZeile & { tiefe: number, maechtigkeit: number };

@Injectable({
  providedIn: 'root'
})
export class ImportService {

	// Cache für Bodenvegetationen-Liste
	private vegetationenCache: string[] | null = null;

  private ds = inject(DataService);
	private ts = inject(TransformationService);
	private fileExport = inject(FileExportService);

	// Bekannte Bodenform-Suffixe (ohne führenden "/")
	private readonly KNOWN_SUFFIXES = ['L', 'lS', 'E', 'KL', 'K', 'C', 'O', 'KO', 'T', 'l', 'ls', 'e', 'kl', 'k', 'c', 'o', 'ko', 't', 'S', 'KS'];

	/**
	 * Entfernt bekannte Bodenform-Suffixe aus einem Substrat-String.
	 * z.B. "S/L" -> "S", "lfS/lS" -> "lfS"
	 */
	private removeSubstratSuffix(substrat: string): string {
		if (!substrat) return substrat;
		let result = substrat.toString().trim();
		// Prüfe ob am Ende "/suffix" steht
		const slashIndex = result.lastIndexOf('/');
		if (slashIndex !== -1) {
			const potentialSuffix = result.substring(slashIndex + 1).trim();
			if (this.KNOWN_SUFFIXES.includes(potentialSuffix)) {
				result = result.substring(0, slashIndex).trim();
			}
		}
		return result;
	}

	// Cache für bekannte HBFs (für intelligentes Parsing)
	private knownHbfsCache: Set<string> | null = null;

	/** Lädt die bekannten HBFs aus der JSON-Datei. Erkennt zusätzlich Legacy-Disjunktiv-Codes (Stil "os V o. K") aus älteren Datenständen. */
	private getKnownHbfs(): Set<string> {
		if (!this.knownHbfsCache) {
			this.knownHbfsCache = new Set([
				...Object.keys(Hauptbodenformen),
				...Object.keys(LEGACY_HBF_CODE_MAP),
			]);
		}
		return this.knownHbfsCache;
	}

	/**
	 * Parst eine HBF-Zeichenkette in einzelne HBFs unter Berücksichtigung von "/" innerhalb von HBFs.
	 * z.B. "s/l F" ist EINE HBF, aber "s/l F/s RB" sind ZWEI HBFs getrennt durch "/"
	 * "//" ist immer ein Trenner zwischen HBFs.
	 */
	private parseHbfString(input: string): Array<{trenner: ''|'/'|'//'; hbf: string}> {
		if (!input) return [];

		const remaining = input.toString().trim();
		const knownHbfs = this.getKnownHbfs();
		const result: Array<{trenner: ''|'/'|'//'; hbf: string}> = [];

		// Zuerst nach "//" aufteilen (immer ein Trenner)
		const doubleSlashParts = remaining.split('//');

		for (let partIndex = 0; partIndex < doubleSlashParts.length; partIndex++) {
			const part = doubleSlashParts[partIndex].trim();
			if (!part) continue;

			const trennerForPart: ''|'/'|'//' = partIndex === 0 && result.length === 0 ? '' : '//';

			// Innerhalb jedes Teils: Prüfe ob "/" ein Trenner ist oder zur HBF gehört
			// Strategie: Versuche die längste bekannte HBF von links zu matchen
			let currentPart = part;
			let isFirst = true;

			while (currentPart.length > 0) {
				let matched = false;

				// Versuche die längste bekannte HBF zu finden
				for (let len = currentPart.length; len > 0; len--) {
					const candidate = currentPart.substring(0, len).trim();
					if (knownHbfs.has(candidate)) {
						const actualTrenner = isFirst ? trennerForPart : '/';
						result.push({ trenner: actualTrenner as ''|'/'|'//', hbf: candidate });
						currentPart = currentPart.substring(len).trim();
						// Entferne führendes "/" wenn vorhanden
						if (currentPart.startsWith('/')) {
							currentPart = currentPart.substring(1).trim();
						}
						isFirst = false;
						matched = true;
						break;
					}
				}

				// Wenn keine bekannte HBF gefunden, nimm alles bis zum nächsten "/"
				if (!matched) {
					const nextSlash = currentPart.indexOf('/');
					if (nextSlash === -1) {
						// Rest ist eine unbekannte HBF
						if (currentPart.trim()) {
							result.push({ trenner: isFirst ? trennerForPart : '/', hbf: currentPart.trim() });
						}
						break;
					} else {
						// Nimm bis zum "/" als HBF
						const hbf = currentPart.substring(0, nextSlash).trim();
						if (hbf) {
							result.push({ trenner: isFirst ? trennerForPart : '/', hbf });
						}
						currentPart = currentPart.substring(nextSlash + 1).trim();
						isFirst = false;
					}
				}
			}
		}

		return result;
	}

	/**
	 * Parst eine kombinierte Feinbodenform-Zeichenkette in Präfix, FBFs (mit Trennern) und Suffix.
	 * Format: [präfix][FBF1][/|//][FBF2][/suffix]
	 * Beispiele:
	 *   "tpBmSE"           -> { prefix: "tp", bfPairs: [{trenner:'', fbf:"BmSE"}], suffix: "" }
	 *   "BmSE/l"           -> { prefix: "", bfPairs: [{trenner:'', fbf:"BmSE"}], suffix: "/l" }
	 *   "BmSE fr"          -> { prefix: "", bfPairs: [{trenner:'', fbf:"BmSE"}], suffix: "" } (Mesoklima entfernt)
	 *   "AbSB/HiWB/L"      -> { prefix: "", bfPairs: [{...fbf:"AbSB"}, {trenner:'/', fbf:"HiWB"}], suffix: "/L" }
	 */
	private parseFeinbodenform(input: string): { prefix: string; bfPairs: Array<{trenner: ''|'/'|'//'; fbf: string}>; suffix: string; reliktG: boolean } {
		if (!input) return { prefix: '', bfPairs: [], suffix: '', reliktG: false };

		let remaining = input.toString().trim();
		let suffix = '';

		// 1a. "(G)" erkennen und entfernen (eigenständiges Merkmal, kein FBF-Suffix)
		const reliktG = /\(G\)/.test(remaining);
		if (reliktG) {
			remaining = remaining.replace(/\s*\(G\)\s*/, '').trim();
		}

		// 1b. Prüfen ob am Ende ein bekanntes Suffix steht (nach letztem "/" aber nicht "//")
		// Suche letztes einzelnes "/" (nicht Teil von "//")
		const lastSingleSlash = this.findLastSingleSlash(remaining);
		if (lastSingleSlash !== -1) {
			const potentialSuffix = remaining.substring(lastSingleSlash + 1).trim();
			if (this.KNOWN_SUFFIXES.includes(potentialSuffix)) {
				suffix = '/' + potentialSuffix;
				remaining = remaining.substring(0, lastSingleSlash);
			}
		}

		// 2. Präfix extrahieren
		const prefixes = ['Ap', 'po', 'rb', 'tp', 'b', 'g', 'h', 'o', 'p', 'q', 's', 'w', 'a', 'x'];
		let prefix = '';

		let foundPrefix = true;
		while (foundPrefix && remaining.length > 0) {
			foundPrefix = false;
			for (const p of prefixes) {
				if (remaining.startsWith(p)) {
					// "a" darf nicht vor "p" matchen — sonst rutscht "ap" als a+p durch.
					// "Ap" steht weiter vorn in der Liste und greift dort, wo es soll.
					if (p === 'a' && remaining.length > 1 && remaining[1] === 'p') continue;
					const afterPrefix = remaining.substring(p.length);
					if (afterPrefix.length === 0 || /^[A-ZÄÖÜ]/.test(afterPrefix) || prefixes.some(px => afterPrefix.startsWith(px))) {
						prefix += p;
						remaining = afterPrefix;
						foundPrefix = true;
						break;
					}
				}
			}
		}

		// 3. FBFs mit Trennern parsen (// zuerst, dann /)
		const bfPairs: Array<{trenner: ''|'/'|'//'; fbf: string}> = [];

		// Aufteilen nach "//" und "/" unter Beibehaltung der Trenner
		const parts = remaining.split(/(\/\/|\/)/);
		let currentTrenner: ''|'/'|'//' = '';

		for (const part of parts) {
			if (part === '//' || part === '/') {
				currentTrenner = part as '/'|'//';
			} else if (part.trim()) {
				// Trailing Zahlen und Mesoklima (fr/tr, auch mit Leerzeichen) entfernen
				const fbf = part.trim().replace(/\d+$/, '').replace(/\s*(fr|tr)$/i, '').trim();
				if (fbf) {
					bfPairs.push({ trenner: currentTrenner, fbf });
					currentTrenner = '';
				}
			}
		}

		return { prefix, bfPairs, suffix, reliktG };
	}

	/** Findet das letzte einzelne "/" (nicht Teil von "//") */
	private findLastSingleSlash(str: string): number {
		for (let i = str.length - 1; i >= 0; i--) {
			if (str[i] === '/') {
				// Prüfe ob Teil von "//"
				if (i > 0 && str[i - 1] === '/') {
					i--; // Überspringe das "//"
					continue;
				}
				if (i < str.length - 1 && str[i + 1] === '/') {
					continue; // Erstes "/" eines "//"
				}
				return i;
			}
		}
		return -1;
	}

	/**
	 * Berechnet die Levenshtein-Distanz zwischen zwei Strings mit Early-Exit-Optimierung
	 */
	private levenshteinDistance(a: string, b: string, maxDistance: number): number {
		const aLower = a.toLowerCase();
		const bLower = b.toLowerCase();

		// Quick exit für identische Strings
		if (aLower === bLower) return 0;

		// Quick exit wenn Längendifferenz schon zu groß
		if (Math.abs(aLower.length - bLower.length) > maxDistance) return maxDistance + 1;

		// Optimierte Version mit nur 2 Zeilen statt voller Matrix
		let prevRow: number[] = [];
		let currRow: number[] = [];

		for (let j = 0; j <= aLower.length; j++) {
			prevRow[j] = j;
		}

		for (let i = 1; i <= bLower.length; i++) {
			currRow[0] = i;
			let minInRow = i;

			for (let j = 1; j <= aLower.length; j++) {
				const cost = bLower.charAt(i - 1) === aLower.charAt(j - 1) ? 0 : 1;
				currRow[j] = Math.min(
					prevRow[j] + 1,      // Deletion
					currRow[j - 1] + 1,  // Insertion
					prevRow[j - 1] + cost // Substitution
				);
				minInRow = Math.min(minInRow, currRow[j]);
			}

			// Early exit: wenn minimale Distanz in dieser Zeile schon zu groß
			if (minInRow > maxDistance) return maxDistance + 1;

			[prevRow, currRow] = [currRow, prevRow];
		}

		return prevRow[aLower.length];
	}

	/**
	 * Extrahiert die Teile eines Pflanzennamens (lateinisch und deutsch)
	 * z.B. "Vaccinium myrtillus (Blaubeere)" → ["vaccinium myrtillus", "blaubeere"]
	 * oder "Blaubeere (Vaccinium myrtillus)" → ["blaubeere", "vaccinium myrtillus"]
	 */
	private extractNameParts(name: string): string[] {
		const lower = name.toLowerCase().trim();
		const parts: string[] = [];

		// Prüfen ob Klammern vorhanden
		const klammerMatch = lower.match(/^([^(]+)\s*\(([^)]+)\)\s*$/);
		if (klammerMatch) {
			parts.push(klammerMatch[1].trim());
			parts.push(klammerMatch[2].trim());
		} else {
			parts.push(lower);
		}

		return parts;
	}

	/**
	 * Bereinigt Vegetationsnamen von Häufigkeitsangaben wie ">= 5", "<= 3" etc.
	 */
	private cleanVegetationInput(input: string): string {
		return input
			.replace(/[<>=]+\s*\d+/g, '')  // >= 5, <= 3, > 2, etc.
			.replace(/\d+\s*[<>=]+/g, '')  // 5 >=, 3 <=, etc.
			.trim();
	}

	/**
	 * Findet den ähnlichsten Vegetationsnamen in der Liste
	 * Gibt den Originalwert zurück, wenn kein ähnlicher gefunden wird
	 */
	private matchVegetation(input: string): string {
		if (!input || input.trim() === '') return input;

		// Cache laden falls noch nicht vorhanden
		if (!this.vegetationenCache) {
			this.vegetationenCache = this.ds.getHardcoded('Bodenvegetationen') as string[];
		}

		// Häufigkeitsangaben entfernen
		const inputTrimmed = this.cleanVegetationInput(input);
		if (!inputTrimmed) return input;

		const inputLower = inputTrimmed.toLowerCase();

		// Exakter Match (case-insensitive)
		const exactMatch = this.vegetationenCache.find(v => v.toLowerCase() === inputLower);
		if (exactMatch) {
			return exactMatch;
		}

		// Teile des Eingabenamens extrahieren (lateinisch/deutsch)
		const inputParts = this.extractNameParts(inputTrimmed);

		// Prüfen ob Input exakt in einem Listeneintrag enthalten ist (als Teilstring)
		for (const vegetation of this.vegetationenCache) {
			const vegLower = vegetation.toLowerCase();
			// Exakter Teilstring-Match (z.B. "Rubus fruticosus" in "...Rubus fruticosus agg.)")
			if (vegLower.includes(inputLower)) {
				console.log(`Vegetation gematcht via Teilstring: "${inputTrimmed}" → "${vegetation}"`);
				return vegetation;
			}
		}

		// Fuzzy-Match auf den Hauptteil (erster Teil, ohne Klammern)
		const mainPart = inputParts[0];
		let bestMatch: string | null = null;
		let bestDistance = Infinity;
		const maxDistance = Math.max(2, Math.floor(mainPart.length * 0.25));

		for (const vegetation of this.vegetationenCache) {
			const vegParts = this.extractNameParts(vegetation);

			for (const vegPart of vegParts) {
				// Schnelle Vorfilterung
				if (Math.abs(vegPart.length - mainPart.length) > maxDistance) continue;

				const distance = this.levenshteinDistance(mainPart, vegPart, bestDistance - 1);
				if (distance < bestDistance) {
					bestDistance = distance;
					bestMatch = vegetation;
					if (distance <= 1) break;
				}
			}
			if (bestDistance <= 1) break;
		}

		if (bestMatch && bestDistance <= maxDistance) {
			console.log(`Vegetation korrigiert: "${inputTrimmed}" → "${bestMatch}" (Distanz: ${bestDistance})`);
			return bestMatch;
		}

		// Kein Match gefunden - Originalwert zurückgeben (wird dann als Validierungsfehler angezeigt)
		console.warn(`Keine Übereinstimmung gefunden für Vegetation: "${inputTrimmed}"`);
		return inputTrimmed;
	}

	/**
	 * Speichert Text-Datei mit Speicherort-Auswahl und optionalem Teilen
	 */
	async saveTextFile(fileName: string, content: string): Promise<void> {
		await this.fileExport.saveOrShare({
			filename: fileName,
			mimeType: 'text/plain',
			content: content
		});
	}

	/**
	 * @deprecated Verwende saveTextFile() für neue Funktionen
	 */
	downloadTextFile(fileName: string, content: string) {
		this.fileExport.download({
			filename: fileName,
			mimeType: 'text/plain',
			content: content
		});
	}

	async exportToXlsx(bps: any[]) {
		const xlsx = await ladeXlsx();
		const url = "assets/xlsx/Bohrpunkt.xlsx";
		const data = await (await fetch(url)).arrayBuffer();
		const wb: XLSX.WorkBook = xlsx.read(data, { type: 'binary', cellStyles: true, cellDates: true });
		const tmplsheet = wb.Sheets['tmpl'];
		const sheetNames: string[] = [];
		const setCell = (sh: XLSX.WorkSheet, c: string, v?: string|number) => {
			if (sh[c].t === 'z') {
				if (typeof v === 'string') sh[c].t = 's';
				if (typeof v === 'number') sh[c].t = 'n';
			}
			const type = sh[c].t;
			switch (type) {
				case 's': 
					sh[c].h = sh[c].v = sh[c].w = v;
					sh[c].r = `<t>${v}</t>`;
					break;
				case 'n': 
					sh[c].v = v; 
					sh[c].w = `${v}`; 
					break;
				case 'z': 
					if (!v==null) 
						alert(`Unknown cell type "${type}" [${c}]!`); 
					break;
				default: alert(`Unknown cell type "${type}" [${c}]!`);
				}
		};
		bps.forEach((bp: AufnahmePunktDocument) => {
			const num = bp.header.nummer;
			wb.SheetNames.push(num);
			wb.Sheets[num] = cloneDeep(tmplsheet);
			const sh = wb.Sheets[num];
			setCell(sh, 'D1', bp.header.forstamt); 
			setCell(sh, 'R1', bp.header.revier);
			
			setCell(sh, 'D2', bp.header.forstbetrieb); 
			setCell(sh, 'R2', bp.header.adresse.abteilung);
			setCell(sh, 'T2', bp.header.adresse.unterabteilung);
			setCell(sh, 'U2', bp.header.adresse.teilflaeche);
			
			setCell(sh, 'D3', dateString(bp.header.aufnahme.datum)); 
			setCell(sh, 'R3', bp.header.aufnahme.kartierer);
			
			setCell(sh, 'D4', bp.header.nummer); 
			setCell(sh, 'R4', bp.header.aufnahme.tiefe);

			setCell(sh, 'D5', bp.header.koordinaten.x); 
			setCell(sh, 'O5', bp.header.koordinaten.y); 

			setCell(sh, 'C6', bp.extendedHeader.humusAuflage.ol); 
			setCell(sh, 'C7', bp.extendedHeader.humusAuflage.of); 
			setCell(sh, 'C8', bp.extendedHeader.humusAuflage.oh); 
			
			setCell(sh, 'I6', bp.extendedHeader.geologie.oben); 
			setCell(sh, 'R6', bp.extendedHeader.geologie.trenner); 
			setCell(sh, 'S6', bp.extendedHeader.geologie.unten); 
			
			setCell(sh, 'F7', bp.extendedHeader.morphologie[0]);
			setCell(sh, 'F8', bp.extendedHeader.morphologie[1]);

			setCell(sh, 'E30', bodenformString(bp.header.bodenform, 'hbf'));
			setCell(sh, 'E31', bodenformString(bp.header.bodenform, 'fbf'));
			
			setCell(sh, 'O32', bp.extendedHeader.saugsaum);
			setCell(sh, 'O33', bp.extendedHeader.spiegelTiefe);
			
			setCell(sh, 'H34', bp.extendedHeader.ph?.ort1);
			setCell(sh, 'I34', bp.extendedHeader.ph?.wert1);
			setCell(sh, 'Q34', bp.extendedHeader.ph?.wert2);
			
			setCell(sh, 'D35', bp.header.wasserstufe2);
			setCell(sh, 'E35', bp.header.wasserstufe);
			setCell(sh, 'Q35', bp.extendedHeader.mesoklima);
			setCell(sh, 'D36', bp.extendedHeader.relief.relief);
			
			setCell(sh, 'D37', bp.extendedHeader.stfg.stammFeuchteStufe.first);
			setCell(sh, 'G37', bp.extendedHeader.stfg.stammFeuchteStufe.second);
			setCell(sh, 'E37', bp.extendedHeader.stfg.naehrkraftStufe);
			setCell(sh, 'P37', bp.extendedHeader.stfg.substratGruppe);
			
			setCell(sh, 'D46', altNeuConverter(bp.header.humusform.aufnahmeFlaeche));
			setCell(sh, 'D47', (typeof bp.extendedHeader.baumarten === 'string') ? bp.extendedHeader.baumarten : undefined);
		});
		const sh = wb.Workbook?.Sheets?.at(0);
		if (sh)
			sh.Hidden = 1;

		// Excel als Blob erstellen und mit Speicherort-Auswahl speichern
		const wbout = xlsx.write(wb, { type: 'array', bookType: 'xlsx', cellDates: true });
		const blob = new Blob([wbout], {
			type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
		});

		await this.fileExport.saveOrShare({
			filename: 'export.xlsx',
			mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
			content: blob
		});
	}

	async exportToList(bps: any[]) {
		const lines = new Array<string[]>;
		lines.push(new Array<string>('Forstamt', 'Revier', 'Forstbetrieb', 'Abteilung', 'Unterabteilung', 'Teilfläche', 'Aufnahmedatum', 'Kartierer', 'Nummer', 
			'Tiefe', 'X', 'Y', 'Ol', 'Of', 'Oh', 'Geologie', 'Morphologie', 'Morphologie2', 'HBF', 'FBF', 'Saugsaum', 'Spiegeltiefe', 'pH-Ort', 'pH', 
			'Grundwasserform', 'Stauwasserform', 'Mesoklima', 'Relief', 'STFG', 'Humusform'));
		const tf = (val: string|number|undefined) => val ? val.toString().replaceAll(';', '') : '';
		const push = (line: string[], val:string|number|undefined) => {
			line.push(tf(val));
		};
		bps.forEach((row: any) => {
			const bp = row.doc;
			const line = new Array<string>;
			push(line, bp.header.forstamt); 
			push(line, bp.header.revier);
			
			push(line, bp.header.forstbetrieb); 
			push(line, bp.header.adresse.abteilung);
			push(line, bp.header.adresse.unterabteilung);
			push(line, bp.header.adresse.teilflaeche);
			
			push(line,dateString(bp.header.aufnahme.datum)); 
			push(line, bp.header.aufnahme.kartierer);
			
			push(line, bp.header.nummer); 
			push(line, bp.header.aufnahme.tiefe);

			push(line, bp.header.koordinaten.x); 
			push(line, bp.header.koordinaten.y); 

			push(line, bp.extendedHeader?.humusAuflage?.ol); 
			push(line, bp.extendedHeader?.humusAuflage?.of); 
			push(line, bp.extendedHeader?.humusAuflage?.oh); 
			
			push(line, tf(bp.extendedHeader.geologie.oben) + tf(bp.extendedHeader.geologie.trenner) + tf(bp.extendedHeader.geologie.unten)); 
			
			push(line, bp.extendedHeader.morphologie[0]);
			push(line, bp.extendedHeader.morphologie[1]);

			push(line, bodenformString(bp.extendedHeader?.bodenform, 'hbf'));
			push(line, bodenformString(bp.extendedHeader?.bodenform, 'fbf'));
			
			push(line, bp.extendedHeader?.saugsaum);
			push(line, bp.extendedHeader?.spiegelTiefe);
			
			push(line, bp.extendedHeader?.ph?.ort1);
			push(line, bp.extendedHeader?.ph?.wert1);
			push(line, bp.extendedHeader?.ph?.ort2);
			push(line, bp.extendedHeader?.ph?.wert2);
			
			push(line, bp.extendedHeader?.wasserstufe);
			push(line, bp.extendedHeader?.wasserstufe2);
			push(line, bp.extendedHeader?.mesoklima);
			push(line, bp.extendedHeader?.relief?.relief);
			
			push(line, stfgString(bp));
			
			push(line, altNeuConverter(bp.extendedHeader?.humusForm?.aufnahmeFlaeche));
			lines.push(line);
		});
		let contents = '';
		lines.forEach((line: string[]) => {
			contents += line.join(';') + '\n';
		});
		await this.saveTextFile('Export.txt', contents);
	}
	
	formatCheck(sheet: any[]): boolean {
		return sheet[0]?.A === 'Forstamt' &&  sheet[1]?.A === 'Forstbetrieb' && sheet[2]?.A === 'Aufnahme' && sheet[3]?.A === 'Nummer' &&
			sheet[0]?.I === 'Revier' &&  (sheet[1]?.I === 'Abteilung/UA/TF' || sheet[1]?.I === 'Abteilung') && sheet[2]?.I === 'Kartierer' && sheet[3]?.I === 'Tiefe';
	}

	/**
	 * Parst kombinierte Torfarten wie "h-h/h-r" oder "h-h/r" in 3 separate Felder
	 * Format: überw./beigem./nachr. getrennt durch "/"
	 * Bei Kurzform wie "h-h/r" wird das Präfix (z.B. "h-") vom ersten Teil übernommen
	 * Beispiele:
	 *   "h-h/h-r" -> ["h-h", "h-r", ""]
	 *   "h-h/r"   -> ["h-h", "h-r", ""]  (Präfix "h-" wird übernommen)
	 *   "y-of"    -> ["y-of", "", ""]
	 *   "y-of/gf" -> ["y-of", "y-gf", ""] (Präfix "y-" wird übernommen)
	 */
	private parseTorfarten(torfartRaw: string): [string, string, string] {
		if (!torfartRaw) return ['', '', ''];

		const parts = torfartRaw.split('/');
		if (parts.length === 1) {
			// Nur eine Torfart
			return [parts[0].trim(), '', ''];
		}

		const ueberw = parts[0].trim();
		// Präfix extrahieren: alles bis einschließlich erstem Bindestrich (z.B. "h-" aus "h-h", "y-" aus "y-of")
		const bindestrichIndex = ueberw.indexOf('-');
		const prefix = bindestrichIndex >= 0 ? ueberw.substring(0, bindestrichIndex + 1) : '';

		// Zweite Torfart: wenn sie keinen Bindestrich hat, das Präfix der ersten übernehmen
		let beigem = parts[1]?.trim() || '';
		if (beigem && !beigem.includes('-') && prefix) {
			beigem = prefix + beigem;
		}

		// Dritte Torfart (falls vorhanden)
		let nachr = parts[2]?.trim() || '';
		if (nachr && !nachr.includes('-') && prefix) {
			nachr = prefix + nachr;
		}

		return [ueberw, beigem, nachr];
	}

	/**
	 * Parst Substrat-Kürzel wie "h2s0k0lfS" in Einzelfelder
	 * Format: h[0-9] = Humus, s[0-9] = Skelett, k[0-9] = Karbonat, Rest = Substrat
	 */
	private parseSubstratKuerzel(kuerzel: string): { humus?: number, skelett?: number, karbonat?: number, substrat: string } {
		if (!kuerzel) return { substrat: '' };

		const humusMatch = kuerzel.match(/h(\d)/i);
		const skelettMatch = kuerzel.match(/s(\d)/i);
		const karbonatMatch = kuerzel.match(/k(\d)/i);

		// Substrat ist der Rest nach h/s/k-Angaben (z.B. "lfS", "mfs", "sU")
		// Muster: alles nach dem letzten Ziffern-Buchstaben-Paar
		const substratMatch = kuerzel.match(/(?:h\d)?(?:s\d)?(?:k\d)?(.+)$/i);
		const substrat = substratMatch ? substratMatch[1] : kuerzel;

		return {
			humus: humusMatch ? parseInt(humusMatch[1]) : undefined,
			skelett: skelettMatch ? parseInt(skelettMatch[1]) : undefined,
			karbonat: karbonatMatch ? parseInt(karbonatMatch[1]) : undefined,
			substrat: substrat
		};
	}

	/**
	 * Prüft ob das Sheet ein Moorprotokoll-Format hat
	 */
	moorFormatCheck(sheet: any[]): boolean {
		// Moorprotokolle beginnen mit "Schichtverzeichnis M" in A0
		const isSchichtverzeichnis = sheet[0]?.A?.toString().startsWith('Schichtverzeichnis M');
		const hasBearbeiter = sheet[0]?.E === 'Bearbeiter';
		const hasRW = sheet[3]?.A === 'RW';
		const hasMoormaech = sheet[4]?.A?.toString().startsWith('Moormächtigkeit');
		return isSchichtverzeichnis && hasBearbeiter && hasRW && hasMoormaech;
	}

	/**
	 * Erkennt das Format eines Sheets automatisch
	 */
	detectSheetFormat(sheet: any[]): 'bohrpunkt' | 'moorpunkt' | 'unknown' {
		if (this.formatCheck(sheet)) return 'bohrpunkt';
		if (this.moorFormatCheck(sheet)) return 'moorpunkt';
		return 'unknown';
	}

	private korrigiereWasserformen(
  d35: number | null,
  e35: number | null,
  substratgruppe: string
	): { wasserstufe2: string | undefined, wasserstufe: string | undefined } {

  const grundwasserSubstrate = ['S', 'bS', 'lS', 'G', 'bG', 'F', 'bF', 'R', 'H'];
  const stauwasserSubstrate = ['K', 'dK', 'tK', 'L', 'dL', 'tL', 'T', 'dT', 'tT'];

  const nurStauwasser = [37];
  const nurGrundwasser = [11, 22, 33, 44, 45, 55, 56, 67, 7];
  const overlap = [12, 13, 14, 15, 16, 17, 23, 24, 25, 26, 27, 34, 35, 36, 46, 47, 57];

  if (d35 && e35) {
    return { wasserstufe2: d35.toString(), wasserstufe: e35.toString() };
  }

  if (d35 && !e35 && nurGrundwasser.includes(d35)) {
    return { wasserstufe2: undefined, wasserstufe: d35.toString() };
  }

  if (!d35 && e35 && nurStauwasser.includes(e35)) {
    return { wasserstufe2: e35.toString(), wasserstufe: undefined };
  }

  if (d35 && !e35 && overlap.includes(d35)) {
    if (grundwasserSubstrate.includes(substratgruppe)) {
      return { wasserstufe2: undefined, wasserstufe: d35.toString() };
    }
    return { wasserstufe2: d35.toString(), wasserstufe: undefined };
  }

  if (!d35 && e35 && overlap.includes(e35)) {
    if (stauwasserSubstrate.includes(substratgruppe)) {
      return { wasserstufe2: e35.toString(), wasserstufe: undefined };
    }
    return { wasserstufe2: undefined, wasserstufe: e35.toString() };
  }

  return { wasserstufe2: d35?.toString(), wasserstufe: e35?.toString() };
}
	
	/**
	 * Parst ein Moorprotokoll aus einem Excel-Sheet
	 */
	moorpunktFromExcelSheet(sheet: any[]): AufnahmePunkt|undefined {
		if (!this.moorFormatCheck(sheet))
			return;

		// Datum parsen (XLSX mit cellDates:true liefert bereits Date-Objekte)
		let date: Date;
		const rawDate = sheet[0].H;
		if (rawDate instanceof Date) {
			date = rawDate;
		} else if (typeof rawDate === 'number') {
			// Excel-Seriennummer -> JS Date
			date = new Date((rawDate - 25569) * 86400 * 1000);
		} else if (typeof rawDate === 'string') {
			date = new Date(rawDate);
		} else {
			date = new Date(); // Fallback auf aktuelles Datum
		}
		date.setHours(date.getHours() + 10);
		const dateStr = date.toISOString();

		const pTyp = '7'; // Moorpunkt (type = Nummer seit v16)

		// Koordinaten parsen
		// Sheet-Struktur: Zeile 3 (Index 3) enthält "RW" in A, Rechtswert in B, "HW" in D, Hochwert in E
		// Format: RW	423449			HW	5958442

		// Koordinaten können als Zahl, String mit Punkt oder String mit Komma vorliegen
		const parseCoord = (val: any): number => {
			if (typeof val === 'number') return Math.round(val);
			if (typeof val === 'string') {
				// Komma durch Punkt ersetzen und parsen
				return Math.round(parseFloat(val.replace(',', '.')));
			}
			return 0;
		};

		// RW (Rechtswert/Easting) und HW (Hochwert/Northing) aus Zeile 4 (Index 3)
		const rawX = parseCoord(sheet[3].B); // RW in B4
		const rawY = parseCoord(sheet[3].F); // HW in F4

		console.log(`Moorpunkt Koordinaten gelesen: RW=${rawX}, HW=${rawY}`);

		if (!rawX || !rawY) throw new Error("Fehlende Koordinaten!");

		// Koordinaten zu EPSG5650 konvertieren
		// RW/HW-Format deutet auf EPSG:25833 (UTM Zone 33N) hin
		let conv2EPSG5650 = new Point(rawX, rawY);

		// Typische EPSG:25833 Bereiche für Deutschland (insb. Ostdeutschland):
		// X (Rechtswert): ca. 200.000 - 600.000
		// Y (Hochwert): ca. 5.600.000 - 6.100.000
		const isLikelyEPSG25833 = rawX >= 200000 && rawX <= 800000 && rawY >= 5000000 && rawY <= 7000000;

		if (isLikelyEPSG25833) {
			console.log(`Koordinaten als EPSG:25833 erkannt, konvertiere zu EPSG:5650...`);
			conv2EPSG5650 = this.ts.toEPSG5650(this.ts.from({ system: LocationSystem.EPSG25833, x: rawX, y: rawY }));
			console.log(`Konvertiert: X=${conv2EPSG5650.x}, Y=${conv2EPSG5650.y}`);
		} else if (this.ts.maybeEPSG5650(rawX, rawY)) {
			conv2EPSG5650 = new Point(rawX, rawY);
		} else {
			// Versuche als bereits konvertierte Koordinaten zu behandeln
			console.warn(`Unbekanntes Koordinatensystem: X=${rawX}, Y=${rawY}`);
			conv2EPSG5650 = new Point(rawX, rawY);
		}

		// Abteilung/UA/TF parsen (z.B. "1210 a 2" -> abt="1210", ua="a", tfl="2")
		const abtString = sheet[1].H?.toString() || '';
		const abtMatch = abtString.match(/^(\d+)\s*([a-z]?)\s*(\d*)$/i);
		const abteilung = abtMatch ? abtMatch[1] : abtString;
		const unterabteilung = abtMatch ? abtMatch[2] : '';
		const teilflaeche = abtMatch ? abtMatch[3] : '';

		// Wasserstufe aus WST parsen (E7, Index 6)
		// Format: "35" = nur Grundwasserstufe, "3/5" = Stauwasser/Grundwasser
		const wstValue = sheet[6].E?.toString().trim() || '';
		let wasserstufe = '';  // Grundwasserstufe
		let wasserstufe2 = ''; // Stauwasserstufe
		if (wstValue.includes('/')) {
			// Format mit Schrägstrich: Stauwasser/Grundwasser
			const parts = wstValue.split('/');
			wasserstufe2 = parts[0].trim(); // Stauwasser (vor dem /)
			wasserstufe = parts[1].trim();  // Grundwasser (nach dem /)
		} else {
			// Ohne Schrägstrich: alles ist Grundwasserstufe
			wasserstufe = wstValue;
		}

		// Karbonat-Tiefe parsen (z.B. "110" oder "-")
		const karbonatValue = sheet[7].E;
		const karbonatTiefe = karbonatValue === '-' ? undefined : parseInt(karbonatValue) || undefined;

		// STGR (Stammstandortformengruppe) parsen (G7, Index 6)
		// Format: z.B. "OA4" -> Stammfeuchte1: O, Nährkraft: A, Stammfeuchte2: 4
		// oder "MA34" -> Stammfeuchte1: M, Nährkraft: A, Stammfeuchte2: 34
		const stgrValue = sheet[6].G?.toString().trim() || '';
		let stammFeuchte1 = '';
		let naehrkraft = '';
		let stammFeuchte2 = '';
		if (stgrValue.length >= 2) {
			stammFeuchte1 = stgrValue[0];           // 1. Zeichen: Stammfeuchte
			naehrkraft = stgrValue[1];              // 2. Zeichen: Nährkraft
			stammFeuchte2 = stgrValue.substring(2); // Rest: Stammfeuchte 2
		} else if (stgrValue.length === 1) {
			stammFeuchte1 = stgrValue;
		}

		const header: Header = {
			nummer: sheet[2].H?.toString() || '',
			forstamt: sheet[1].E?.toString() || '',
			forstbetrieb: '',
			revier: sheet[2].E?.toString() || '',
			adresse: {
				gemarkung: '',
				abteilung: abteilung,
				unterabteilung: unterabteilung,
				teilflaeche: teilflaeche
			},
			koordinaten: {
				system: LocationSystem.EPSG5650,
				x: conv2EPSG5650.x,
				y: conv2EPSG5650.y
			},
			aufnahme: {
				datum: dateStr,
				kartierer: sheet[0].F?.toString() || '',
				tiefe: parseInt(sheet[4].D) || 0 // Moormächtigkeit als Tiefe
			},
			bodenform: (() => {
				const parsed = this.parseFeinbodenform(sheet[6].C?.toString() || '');
				const bf = parsed.bfPairs.length > 0
					? parsed.bfPairs.map(p => ({ trenner: p.trenner, hbf: '', fbf: p.fbf }))
					: [{ trenner: '' as const, hbf: '', fbf: '' }];
				if (parsed.prefix && bf.length > 0) {
					(bf[0] as any).prefix = parsed.prefix;
				}
				return {
					bf,
					suffix: parsed.suffix || undefined,
					reliktG: parsed.reliktG || undefined
				};
			})(),
			humusform: {
				aufnahmeFlaeche: { neu: '' } // Moorpunkte haben Vegetationsformengruppe statt Humusform
			},
			vegetationsformengruppe: sheet[41].C?.toString() || '', // Veg-FG
			wasserstufe: wasserstufe || undefined,
			wasserstufe2: wasserstufe2 || undefined,
			substratFeuchte: '',
			bemerkungen: sheet[42].C?.toString() || '',
			moorMaechtigkeit: parseInt(sheet[4].D) || undefined,
		};

		const extendedHeader: ExtendedHeader = {
			geologie: {
				oben: '',
				trenner: undefined,
				unten: undefined,
			},
			morphologie: [],
			relief: {
				relief: '',
				reliefBestand: sheet[7].G?.toString() || '',        // Relief Bst.
				reliefBodenvegetation: sheet[7].H?.toString() || '', // Relief Bod.-veg
			},
			humusAuflage: {
				ol: parseInt(sheet[9].D) || 0,
				of: parseInt(sheet[9].F) || 0,
				oh: parseInt(sheet[9].H) || 0
			},
			klimaStufe: { neu: '' }, // Wird von der DB gefetcht
			stfg: {
				stammFeuchteStufe: { first: stammFeuchte1, second: stammFeuchte2 },
				naehrkraftStufe: naehrkraft,
				substratGruppe: '' // Bei Moorpunkten nicht separat angegeben
			},
			baumarten: sheet[40].C?.toString() || '', // Bestand
			entwaesserungsverhalten: 'II', // Standard für Moorpunkte
			kMgCaPSerie: '',

			// Pflichtfelder für ExtendedHeader (nicht relevant für Moorpunkte)
			nWsk: 0,
			nWskStufe: '',
			mesoklima: '',
			gHorizont: { go: '', gr: '', g: '' },
			sHorizont: { sw: '', sd: '', s: '' },

			// pH-Wert (für Moorpunkte meist leer, aber Objekt muss existieren)
			ph: {
				wert1: undefined,
				ort1: '',
				wert2: undefined,
				ort2: '',
				durchschnitt: undefined
			},

			// Moor-spezifische Felder
			moorTyp: {
				oekologisch: sheet[8].C?.toString() || '',    // Ökolog. Moortyp
				hydrogenetisch: sheet[8].F?.toString() || '', // Hydrogen. Moortyp
			},
			karbonat: karbonatTiefe,
			witterung: sheet[5].G?.toString() || '',
			spiegelTiefe: parseInt(sheet[4].G) || undefined, // Spiegeltiefe aus G5 (Index 4)
		};

		// Substratfolge und Horizontfolge parsen (ab Zeile 15)
		// Dynamisch bis zur ersten leeren Tiefe-Zelle oder Vegetation-Header
		const Substratfolge = new Array<SubstratZeile>();
		const Horizontfolge = new Array<ImportHorizontZeile>();

		for (let i = 0; i < 15; ++i) {
			const row = sheet[14 + i];
			if (!row) continue;
			// Stopp wenn "Vegetation" Header erreicht (Spalte A)
			if (row.A?.toString()?.toLowerCase().startsWith('vegetation')) break;
			const tiefeRaw = row.A;
			const tiefe = parseInt(tiefeRaw) || 0;

			// Horizont aus Spalte E in Horizontfolge
			// Zusammenfassen wenn gleicher Horizont wie vorherige Zeile
			const horizontStr = row.E?.toString()?.trim() || '';
			// Horizont verarbeiten wenn vorhanden und Tiefe gültig
			if (horizontStr && tiefe > 0) {
				const lastHorizont = Horizontfolge.length > 0 ? Horizontfolge[Horizontfolge.length - 1] : null;
				if (lastHorizont && lastHorizont.horizonte[0] === horizontStr) {
					// Gleicher Horizont wie vorher -> Tiefe aktualisieren (zusammenfassen)
					lastHorizont.tiefe = tiefe;
				} else {
					// Neuer Horizont
					Horizontfolge.push({
						id: neueZeilenKennung(),
						tiefe: tiefe,
						maechtigkeit: 0,
						horizonte: [horizontStr, '', '']
					});
				}
			}

			if (tiefe > 0) {
				// Torfart aus Spalte B (z.B. "h-a", "h-h/h-r", "h-h/r", "y-of")
				const torfartRaw = row.B?.toString() || '';

				// Prüfen ob Mudde (y-) oder Torf (h-)
				const isMudde = torfartRaw.startsWith('y');
				const isTorf = torfartRaw.startsWith('h') && !torfartRaw.match(/h\ds\dk\d/i); // h- aber nicht h2s0k0...

				// Zersetzungsgrad (für Torf) / Konsistenz (für Mudde) aus Spalte D
				// Manchmal steht "K" vor der Konsistenz (z.B. "K3"), das entfernen
				const spalteD = row.D?.toString()?.replace(/^K/i, '') || '';
				const spalteD_num = parseInt(spalteD) || undefined;

				// Zersetzungsgrad nur für Torf, Konsistenz nur für Mudde
				const zersetzungsgrad = isTorf ? spalteD_num : undefined;
				const muddeKonsistenz = isMudde ? spalteD_num : undefined;

				// Prüfen ob mineralisches Substrat (Kürzel wie "h2s0k0lfS")
				// Erkennbar an: Torfart enthält h[0-9]s[0-9]k[0-9] oder Substrat ist "S" oder "L"
				const substratRaw = row.C?.toString() || '';
				const isMineralisch = torfartRaw.match(/h\ds\dk\d/i) || substratRaw === 'S' || substratRaw === 'L';

				let substrat = this.removeSubstratSuffix(substratRaw);
				let humusgehalt: number | undefined;
				let skelettgehalt = 0;
				let karbonatgehalt = 0;
				let torfarten: [string, string, string] = ['', '', ''];

				if (isMineralisch && torfartRaw.match(/h\ds\dk\d/i)) {
					// Mineralisches Substrat: parse Kürzel aus Torfart-Spalte
					const parsed = this.parseSubstratKuerzel(torfartRaw);
					substrat = parsed.substrat || substratRaw;
					humusgehalt = parsed.humus;
					skelettgehalt = parsed.skelett ?? 0;
					karbonatgehalt = parsed.karbonat ?? 0;
				} else if (!isMineralisch) {
					// Organisches Substrat: Torfarten parsen (z.B. "h-h/h-r" oder "h-h/r")
					torfarten = this.parseTorfarten(torfartRaw);
				}

				Substratfolge.push({
					id: neueZeilenKennung(),
					tiefe: tiefe,
					maechtigkeit: 0, // Wird nicht separat angegeben im Moorprotokoll
					substrat: substrat,
					wechselschichtung: false,
					humusgehalt: humusgehalt,
					lagerungsdichte: parseInt(row.G) || undefined, // SV / Lagerungsdichte
					skelettgehalt: skelettgehalt,
					karbonatgehalt: karbonatgehalt,
					besonderheiten: row.F?.toString() || '', // Spalte F: Besonderheiten
					torfarten: torfarten,
					torfZersetzungsgrad: zersetzungsgrad,
					muddeKonsistenz: muddeKonsistenz,
					substanzvolumen: parseInt(row.G) || undefined, // SV bei Moorpunkten
					farbe: row.H?.toString() || '',
				});
			}
		}

		// Vegetation parsen (Zeilen 27-39, zwei Spalten)
		const bodenvegetation: BodenVegetation = {
			aufnahme: { schichtAnteile: {}, vegetationen: new Array<BodenvegetationZeile>() }
		};
		for (let i = 0; i < 13; ++i) {
			const row = sheet[27 + i];
			if (!row || row.__rowNum__ >= 40) break;
			// Linke Spalte (A-E)
			if (row.A) {
				bodenvegetation.aufnahme.vegetationen.push({
					id: neueZeilenKennung(),
					vegetation: this.matchVegetation(row.A?.toString() || ''),
					haeufigkeit: row.E?.toString() || ''
				});
			}
			// Rechte Spalte (F-H)
			if (row.F) {
				bodenvegetation.aufnahme.vegetationen.push({
					id: neueZeilenKennung(),
					vegetation: this.matchVegetation(row.F?.toString() || ''),
					haeufigkeit: row.H?.toString() || ''
				});
			}
		}

		return {
			version: CURRENT_VERSION,
			type: pTyp,
			quality: 1,
			header: header,
			extendedHeader: extendedHeader,
			Substratfolge: Substratfolge,
			Horizontfolge: Horizontfolge,
			bodenvegetation: bodenvegetation
		};
	}

	/**
	 * Prüft ob zwei SubstratZeilen identische Eigenschaften haben (außer Tiefe/Mächtigkeit).
	 * Wird verwendet um bei 160cm geteilte Schichten wieder zusammenzuführen.
	 */
	private substratZeilenIdentisch(a: SubstratZeile, b: SubstratZeile): boolean {
		return a.substrat === b.substrat &&
			a.wechselschichtung === b.wechselschichtung &&
			a.humusgehalt === b.humusgehalt &&
			a.lagerungsdichte === b.lagerungsdichte &&
			a.skelettgehalt === b.skelettgehalt &&
			a.karbonatgehalt === b.karbonatgehalt &&
			a.besonderheiten === b.besonderheiten &&
			JSON.stringify(a.torfarten) === JSON.stringify(b.torfarten);
	}

	/**
	 * Prüft ob zwei HorizontZeilen identische Eigenschaften haben (außer Tiefe/Mächtigkeit).
	 */
	private horizontZeilenIdentisch(a: HorizontZeile, b: HorizontZeile): boolean {
		return JSON.stringify(a.horizonte) === JSON.stringify(b.horizonte);
	}

	/**
	 * Führt bei 160cm geteilte Substratschichten wieder zusammen.
	 * Im Excel wurde bei 160cm ein Bruch gemacht - Schichten die über 160cm hinausgingen
	 * wurden in zwei Zeilen aufgeteilt. Diese Funktion fügt sie wieder zusammen.
	 */
	private mergeSubstratfolge160(folge: SubstratZeile[]): SubstratZeile[] {
		if (folge.length < 2) return folge;

		const result: SubstratZeile[] = [];
		let i = 0;

		while (i < folge.length) {
			const current = folge[i];

			// Prüfe ob aktuelle Zeile bei 160 endet und nächste bei 160 beginnt
			if (current.tiefe === 160 && i + 1 < folge.length) {
				const next = folge[i + 1];
				// Berechne wo die nächste Schicht beginnt (Tiefe - Mächtigkeit oder vorherige Tiefe)
				const nextStart = next.maechtigkeit > 0 ? next.tiefe - next.maechtigkeit : current.tiefe;

				if (nextStart === 160 && this.substratZeilenIdentisch(current, next)) {
					// Zusammenführen: Tiefe der zweiten übernehmen, Mächtigkeit addieren
					const merged: SubstratZeile = {
						...current,
						tiefe: next.tiefe,
						maechtigkeit: current.maechtigkeit + next.maechtigkeit
					};
					result.push(merged);
					console.log(`Substrat bei 160cm zusammengeführt: ${current.tiefe}→${next.tiefe}cm (${current.substrat})`);
					i += 2; // Beide Zeilen verarbeitet
					continue;
				}
			}

			result.push(current);
			i++;
		}

		return result;
	}

	/**
	 * Führt bei 160cm geteilte Horizontschichten wieder zusammen.
	 */
	private mergeHorizontfolge160(folge: ImportHorizontZeile[]): ImportHorizontZeile[] {
		if (folge.length < 2) return folge;

		const result: ImportHorizontZeile[] = [];
		let i = 0;

		while (i < folge.length) {
			const current = folge[i];

			// Prüfe ob aktuelle Zeile bei 160 endet und nächste bei 160 beginnt
			if (current.tiefe === 160 && i + 1 < folge.length) {
				const next = folge[i + 1];
				const nextStart = next.maechtigkeit > 0 ? next.tiefe - next.maechtigkeit : current.tiefe;

				if (nextStart === 160 && this.horizontZeilenIdentisch(current, next)) {
					// Zusammenführen: Tiefe der zweiten übernehmen, Mächtigkeit addieren
					const merged: ImportHorizontZeile = {
						...current,
						tiefe: next.tiefe,
						maechtigkeit: current.maechtigkeit + next.maechtigkeit
					};
					result.push(merged);
					console.log(`Horizont bei 160cm zusammengeführt: ${current.tiefe}→${next.tiefe}cm (${current.horizonte.join('/')})`);
					i += 2;
					continue;
				}
			}

			result.push(current);
			i++;
		}

		return result;
	}

	/**
	 * Ergebnis des Bohrpunkt-Parsens mit Meta-Informationen.
	 *
	 * - `dateMissing`: true, wenn das Sheet kein Aufnahmedatum hatte. Der Punkt wird
	 *   trotzdem importiert (mit datum=undefined); die UI muss dem User in der
	 *   Validierung anzeigen, dass das Datum nachzutragen ist.
	 */
	lastParseInfo: { coordTransform?: string; pointType?: string; dateMissing?: boolean } = {};

	bohrpunktFromExcelSheet(sheet: Array<any>): AufnahmePunkt|undefined {
		if (!this.formatCheck(sheet))
			return;

		// Reset parse info
		this.lastParseInfo = {};

		// Datum parsen - kann Excel-Datum (Zahl) oder deutsches Datumsformat (String) sein.
		// Soft-Error: wenn Datum fehlt, importieren wir ohne datum und markieren via
		// lastParseInfo.dateMissing — der Wizard zeigt dem User in der Validierung,
		// dass das Datum in der UI nachgetragen werden muss.
		let date: Date | undefined;
		const rawDate = sheet[2]?.D;
		if (rawDate === undefined || rawDate === null || rawDate === '') {
			this.lastParseInfo.dateMissing = true;
		} else if (typeof rawDate === 'string') {
			// Deutsches Format "TT.MM.JJ" oder "TT.MM.JJJJ" parsen
			const parts = rawDate.split('.');
			if (parts.length === 3) {
				const day = parseInt(parts[0]);
				const month = parseInt(parts[1]);
				let year = parseInt(parts[2]);
				// Zweistelliges Jahr: <30 = 2000er, >=30 = 1900er
				if (year < 100) {
					year = year < 30 ? 2000 + year : 1900 + year;
				}
				// Validierung
				if (day < 1 || day > 31) throw new Error(`Ungültiger Tag: ${day} (Datum: ${rawDate})`);
				if (month < 1 || month > 12) throw new Error(`Ungültiger Monat: ${month} (Datum: ${rawDate})`);
				if (year < 1900 || year > 2100) throw new Error(`Ungültiges Jahr: ${year} (Datum: ${rawDate})`);
				date = new Date(year, month - 1, day, 10, 0, 0);
			} else {
				// Fallback: direkt parsen
				date = new Date(rawDate);
			}
		} else {
			// Excel-Datumswert (Zahl oder Date-Objekt)
			date = new Date(rawDate);
			date.setHours(date.getHours() + 10);
		}

		if (date && isNaN(date.getTime())) {
			throw new Error(`Ungültiges Datum: ${rawDate}`);
		}

		// Aufnahme.datum ist im Datenmodell `string` (nicht optional). Wenn das Excel
		// kein Datum hatte, geben wir den Leerstring weiter und markieren via
		// lastParseInfo.dateMissing — der Wizard zeigt dem User die Warnung.
		const dateStr: string = date ? date.toISOString() : '';

    // Moorpunkt-Erkennung: HBF beginnt mit M, S.M, SM oder GM, oder Morphologie enthält "moor"
    const hbf = (sheet[29]?.E || '').toString().trim();
    const morphologie1 = (sheet[6]?.F || '').toString().toLowerCase();
    const morphologie2 = (sheet[7]?.F || '').toString().toLowerCase();
    const isMoorpunkt = /^(M|S\.?M|GM)\b/i.test(hbf) ||
                        morphologie1.includes('moor') ||
                        morphologie2.includes('moor');
    const pTyp = isMoorpunkt ? '7' : '6'; // Moorpunkt / Tiefbohrung (type = Nummer seit v16)

    // Punkttyp-Info speichern
    if (isMoorpunkt) {
      this.lastParseInfo.pointType = 'Moorpunkt (aus Mineralbodenprotokoll)';
    }

    const collectBemerkungenLines = (): string[] => {
      const lines: string[] = [];
      for (let i = 48; i <= 51; i++) {
        const cell = sheet[i]?.A;
        lines.push(cell !== undefined ? String(cell) : '');
      }
      return lines;
    };
    // Aus den Bemerkungen-Zeilen 48–51 zusätzliche Vegetations-Einträge
    // extrahieren (Overflow-Liste, wenn die 14 Slots nicht reichten) und die
    // Prosa-Reste neu zu einem mehrzeiligen Bemerkungen-Text zusammenfügen.
    const bemerkungenParse = this.parseBemerkungenVegetationen(collectBemerkungenLines());
    const bemerkungenText = bemerkungenParse.remainingLines.join('\n');
		if (!sheet[4].D || !sheet[4].O) {
			// Hard-Error: Punkte ohne Koordinaten haben keinen Platz auf der Karte.
			// Nummer mit reinhaengen, damit der User in der Wizard-Validierung sieht,
			// welcher Punkt betroffen ist.
			const nummer = sheet[3]?.D ? ` (Nr. ${sheet[3].D})` : '';
			throw new Error(`Fehlende Koordinaten in Zeile 5${nummer} — Rechtswert (D) und Hochwert (O) muessen gefuellt sein`);
		}

		const rawX = parseInt(sheet[4].D);
		const rawY = parseInt(sheet[4].O);
		const coordSystem = (sheet[4].A || '').toString().toUpperCase();

		// Koordinatensystem erkennen und transformieren
		let conv2EPSG5650: Point;

		if (coordSystem === 'GK' || coordSystem === 'GAUSS-KRÜGER' || coordSystem === 'GAUSS-KRUEGER') {
			// Gauß-Krüger: Zone aus erster Ziffer des Rechtswerts ableiten
			const zone = Math.floor(rawX / 1000000);
			let epsgCode: LocationSystem;
			let zoneName: string;
			switch (zone) {
				case 2: epsgCode = LocationSystem.EPSG31466; zoneName = 'GK Zone 2'; break;
				case 3: epsgCode = LocationSystem.EPSG31467; zoneName = 'GK Zone 3'; break;
				case 4: epsgCode = LocationSystem.EPSG31468; zoneName = 'GK Zone 4'; break;
				case 5: epsgCode = LocationSystem.EPSG31469; zoneName = 'GK Zone 5'; break;
				default:
					throw new Error(`Unbekannte Gauß-Krüger Zone: ${zone} (Rechtswert: ${rawX})`);
			}
			this.lastParseInfo.coordTransform = `${zoneName} (${epsgCode}) → EPSG:5650`;
			console.log(`Import: Koordinaten als ${epsgCode} (${zoneName}) erkannt: R=${rawX}, H=${rawY}`);
			const latLng = this.ts.from({ system: epsgCode, x: rawX, y: rawY });
			conv2EPSG5650 = this.ts.toEPSG5650(latLng);
		} else if (coordSystem === 'UTM' || this.ts.maybeEPSG25833(rawX, rawY)) {
			// UTM Zone 33N (EPSG:25833)
			this.lastParseInfo.coordTransform = 'UTM Zone 33N (EPSG:25833) → EPSG:5650';
			console.log(`Import: Koordinaten als EPSG:25833 (UTM) erkannt: E=${rawX}, N=${rawY}`);
			conv2EPSG5650 = this.ts.toEPSG5650(this.ts.from({ system: LocationSystem.EPSG25833, x: rawX, y: rawY }));
		} else if (this.ts.maybeEPSG5650(rawX, rawY)) {
			// Bereits EPSG:5650
			this.lastParseInfo.coordTransform = 'EPSG:5650 (keine Transformation)';
			console.log(`Import: Koordinaten bereits als EPSG:5650 erkannt: E=${rawX}, N=${rawY}`);
			conv2EPSG5650 = new Point(rawX, rawY);
		} else {
			throw new Error(`Koordinatensystem nicht erkannt (${coordSystem}). Koordinaten: ${rawX}, ${rawY}`);
		}

		const wasserformen = this.korrigiereWasserformen(
			parseInt(sheet[34].D) || null,
			parseInt(sheet[34].E) || null,
			sheet[36].P
		);

    const header: Header = {
      // Eine reine Zahl in D4 kommt als Zahl aus der Tabelle; gespeichert wird Text
      // (Befund 29.09.2026: eine Zahl als Nummer liess die Punktliste abbrechen).
      nummer: sheet[3].D?.toString() || '',
      forstamt: sheet[0].D,
      forstbetrieb: sheet[1].D,
      revier: sheet[0].R,
      adresse: {
        gemarkung: '',
        abteilung: sheet[1].R,
        unterabteilung: sheet[1].T,
        teilflaeche: sheet[1].U
      },
      koordinaten: {
				system: LocationSystem.EPSG5650,
				x: conv2EPSG5650.x,
				y: conv2EPSG5650.y
			},
      aufnahme: {
        datum: dateStr,
        kartierer: sheet[2].R,
        tiefe: parseInt(sheet[3].R)
      },
      bodenform: (() => {
        const parsedFbf = this.parseFeinbodenform(sheet[30].E?.toString() || '');
        const hbfRaw = sheet[29].E?.toString() || '';
        // HBF intelligent parsen (berücksichtigt "/" innerhalb von HBFs wie "s/l F")
        const parsedHbfs = this.parseHbfString(hbfRaw);

        const bf = parsedFbf.bfPairs.length > 0
          ? parsedFbf.bfPairs.map((p, i) => ({
              trenner: p.trenner,
              hbf: parsedHbfs[i]?.hbf || '',  // Passende HBF zum Index
              fbf: p.fbf
            }))
          : [{ trenner: '' as const, hbf: parsedHbfs[0]?.hbf || '', fbf: '' }];

        if (parsedFbf.prefix && bf.length > 0) {
          (bf[0] as any).prefix = parsedFbf.prefix;
        }

        return {
          bf,
          suffix: parsedFbf.suffix || undefined,
          reliktG: parsedFbf.reliktG || undefined
        };
      })(),
      humusform: {
        aufnahmeFlaeche: { neu: sheet[45].D }
      },
			vegetationsformengruppe: '',
			wasserstufe2: wasserformen.wasserstufe2,
  		wasserstufe: wasserformen.wasserstufe,

      substratFeuchte: sheet[30].U,
			
			bemerkungen: bemerkungenText,
    };
		if (!header?.aufnahme?.kartierer) {
			console.log(header);
		}
		
    const extendedHeader: ExtendedHeader = {
      // Geologie: F=oben, I=unten gemaess aktuellem Excel-Template (Stichprobe Mai 2026,
       // BP_Torgelow_*-Beispiele). Legacy-Fallback: wenn F leer ist, war "oben" frueher
       // in I — dann uebernehmen wir das alte Layout (oben aus I, optional trenner=R / unten=S).
      geologie: isEmpty(sheet[5].F)
        ? {
            oben: sheet[5].I,
            trenner: isEmpty(sheet[5].I) ? sheet[5].R : undefined,
            unten: isEmpty(sheet[5].I) ? sheet[5].S : undefined,
          }
        : {
            oben: sheet[5].F,
            unten: !isEmpty(sheet[5].I) ? sheet[5].I : undefined,
          },
      morphologie: [ sheet[6].F, sheet[7].F ],
      relief: {
        relief: sheet[35].D
      },
      humusAuflage: {
        ol: sheet[5].C,
        of: sheet[6].C,
        oh: sheet[7].C
      },
      klimaStufe: { neu: '' },
      stfg: {
        stammFeuchteStufe: {
          first: sheet[36].D,
          second: sheet[36].G
        },
        naehrkraftStufe: sheet[36].E,
        substratGruppe: sheet[36].P
      },
      baumarten: sheet[46].D,
      entwaesserungsverhalten: sheet[29].U,
      kMgCaPSerie: '', // vielleicht ableitbar aus Bodenform

      // `|| undefined`: leere Zellen liefern parseInt/parseFloat=NaN, was im JSON
      // als null landet und weiter unten ungeschickte UI-Effekte hat. Lieber sauberes undefined.
      nWsk: parseFloat(sheet[28].F) || undefined,
      nWskStufe: sheet[28].R,

      saugsaum: parseInt(sheet[31].O) || undefined,
      karbonat: parseInt(sheet[31].S) || undefined,
      spiegelTiefe: parseInt(sheet[32].O) || undefined,
      mesoklima: sheet[34].Q,

      gHorizont: {
        go: sheet[31].B,
        gr: sheet[32].B,
        g: sheet[33].B,
      },
      sHorizont: {
        sw: sheet[31].D,
        sd: sheet[32].D,
        s: sheet[33].D,
      },

      ph: {
        wert1: parseFloat(sheet[33].I) || undefined,
        ort1: sheet[33].H || '',
        wert2: parseFloat(sheet[33].Q) || undefined,
        ort2: '',
        durchschnitt: parseFloat(sheet[33].T) || undefined
      },
    };
		const R = sheet[4].D;
		const H = sheet[4].O;
		const Substratfolge = new Array<SubstratZeile>();
		const Horizontfolge = new Array<ImportHorizontZeile>();
		const bodenvegetation: BodenVegetation = {/*bestandsBeschreibung: '', */aufnahme: { schichtAnteile: {}, vegetationen: new Array<BodenvegetationZeile>() }};
		for (let i = 0; i < 17; ++i) {
			const row = sheet[11 + i];
			if (row.__rowNum__ >= 28) break;
			if (row.B && row.B > 0) {
				Substratfolge.push({
					id: neueZeilenKennung(),
					tiefe: row.B as number,
					maechtigkeit: row.C as number,
					substrat: this.removeSubstratSuffix(row.D),
					wechselschichtung: row.E && row.E !== "" ? true : false,
					humusgehalt: row.F,
					lagerungsdichte: row.G,
					skelettgehalt: row.H,
					karbonatgehalt: row.I,
					besonderheiten: '',
					torfarten: ['', '', '']
				});
			}
			if (row.Q && row.Q > 0) {
				Horizontfolge.push({
					id: neueZeilenKennung(),
					tiefe: row.Q as number,
          maechtigkeit: row.R as number,
					horizonte: [row.S, row.T, row.U]
				});
			}
		}
		for (let i = 0; i < 7; ++i) {
			const row = sheet[38 + i];
			if (!row || row.__rowNum__ >= 45) break;
			if (row.A) {
				bodenvegetation.aufnahme.vegetationen.push({
					id: neueZeilenKennung(),
					vegetation: this.matchVegetation(row.A?.toString() || ''),
					haeufigkeit: row.G
				});
			}
			if (row.H) {
				bodenvegetation.aufnahme.vegetationen.push({
					id: neueZeilenKennung(),
					vegetation: this.matchVegetation(row.H?.toString() || ''),
					haeufigkeit: row.U
				});
			}
		}
		// Overflow-Vegetation aus Bemerkungs-Zeilen (siehe parseBemerkungenVegetationen)
		bodenvegetation.aufnahme.vegetationen.push(...bemerkungenParse.vegetationen);

		// 160cm-Bruch auflösen: Im Excel wurden Schichten bei 160cm geteilt
		const mergedSubstratfolge = this.mergeSubstratfolge160(Substratfolge);
		const mergedHorizontfolge = this.mergeHorizontfolge160(Horizontfolge);

		return {
			version: CURRENT_VERSION,
      type: pTyp,
      quality: 1,
			header: header,
      extendedHeader: extendedHeader,
			Substratfolge: mergedSubstratfolge,
			Horizontfolge: mergedHorizontfolge,
			bodenvegetation: bodenvegetation
		};
	}

	/**
	 * Extrahiert Vegetations-Einträge aus mehrzeiligen Bemerkungen-Texten.
	 *
	 * Hintergrund: Wenn der reservierte Vegetations-Block (Zeilen 38–44, max.
	 * 14 Einträge) nicht reichte, wurden Arten regelmäßig in den Bemerkungs-
	 * Zeilen 48–51 als kommaseparierte Liste nachgetragen, z.B.:
	 *   "Stellaria media +, Impatiens parviflora +, Rubus caesius 1"
	 *
	 * Heuristik: ein Eintrag matcht, wenn er das Muster
	 *   <Capital-Latin> <lowercase-Latin> [optional Modifier oder (deutscher Name)] <häufigkeit>
	 * erfüllt. Was nicht matcht, bleibt als Prosa stehen.
	 *
	 * Häufigkeits-Tokens: +, r, 1..N, r...+, >= N, <= N.
	 *
	 * Robust gegen:
	 *  - trailing Kommas: "X y +, "
	 *  - fehlende Kommas zwischen Einträgen: "X y 1 P q +" wird intern zu "X y 1, P q +"
	 *  - eingestreute Prosa: ungeparste Chunks bleiben in remainingLines.
	 */
	parseBemerkungenVegetationen(rawLines: string[]): {
		remainingLines: string[];
		vegetationen: BodenvegetationZeile[];
	} {
		const haeufigkeitToken = '(?:>=\\s*\\d+|<=\\s*\\d+|r\\.\\.\\.\\+|\\d+|\\+|r)';
		// Eintrag: Genus species [Mod oder (deutsch)]* haeufigkeit
		// Genus = Großbuchstabe + Latin-Kleinbuchstaben (mit Sonderzeichen)
		// species = mind. ein lowercase-Latin-Wort, evtl. mit Bindestrich/Punkt
		const entryRegex = new RegExp(
			`^([A-ZÄÖÜ][a-zäöüß.\\-]+(?:\\s+[a-zäöüß.\\-]+)+(?:\\s*\\([^)]*\\))?(?:\\s+(?:>=\\s*\\d+|<=\\s*\\d+|r\\.\\.\\.\\+))?(?:\\s*\\([^)]*\\))?)` +
			`\\s+(${haeufigkeitToken})\\s*$`
		);
		// Insert-Komma: zwischen <haeufigkeit> und <Capital> — reine Whitespace-Trennung erkennen
		const missingCommaRegex = new RegExp(`(\\s${haeufigkeitToken})\\s+(?=[A-ZÄÖÜ][a-zäöüß])`, 'g');

		const veg: BodenvegetationZeile[] = [];
		const remainingLines: string[] = [];

		for (const rawLine of rawLines) {
			if (!rawLine || !rawLine.trim()) {
				continue;
			}

			// Fehlende Kommas zwischen Einträgen einfügen
			const normalized = rawLine.replace(missingCommaRegex, '$1, ');
			const parts = normalized.split(',');
			const remainingParts: string[] = [];

			for (const part of parts) {
				const chunk = part.trim();
				if (!chunk) continue;

				const m = chunk.match(entryRegex);
				if (m) {
					veg.push({
						vegetation: this.matchVegetation(m[1].trim()),
						haeufigkeit: m[2].trim()
					});
				} else {
					remainingParts.push(chunk);
				}
			}

			if (remainingParts.length > 0) {
				remainingLines.push(remainingParts.join(', '));
			}
		}

		return { remainingLines, vegetationen: veg };
	}

	/**
	 * Findet DB-Bohrpunkte zu einer Excel-Nummer.
	 *
	 * Strategie:
	 *  1. Exakter Match auf header.nummer (case-insensitive, getrimmt).
	 *  2. Falls leer ODER ergänzend: Token-Match — die Excel-Nummer als
	 *     delimitiertes Token (umrandet von -, _, /, ., Whitespace) in der
	 *     DB-Nummer. Deckt Renaming-Schemata ab wie "rh-rm-001-2025" für
	 *     Excel-Nummer "001".
	 *
	 * Liefert ALLE Kandidaten als Array — die Auflösung über Koordinaten
	 * etc. macht der Aufrufer.
	 */
	findNummerMatches<T extends { _id: string; header?: { nummer?: any } }>(
		excelNummer: string,
		allDocs: T[]
	): { candidates: T[]; matchType: 'exact' | 'token' | 'none' } {
		const target = excelNummer.trim().toLowerCase();
		if (!target) return { candidates: [], matchType: 'none' };

		const exact: T[] = [];
		for (const doc of allDocs) {
			const docN = String(doc.header?.nummer ?? '').trim().toLowerCase();
			if (docN && docN === target) exact.push(doc);
		}

		// Token-Match: Excel-Nummer als ganzes Token in der DB-Nummer
		const escaped = excelNummer.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
		const tokenRegex = new RegExp(`(^|[-_/.\\s])${escaped}([-_/.\\s]|$)`, 'i');
		const exactIds = new Set(exact.map(d => d._id));
		const tokenMatches: T[] = [];
		for (const doc of allDocs) {
			if (exactIds.has(doc._id)) continue;
			const docN = String(doc.header?.nummer ?? '');
			if (docN && tokenRegex.test(docN)) tokenMatches.push(doc);
		}

		if (exact.length === 0 && tokenMatches.length === 0) {
			return { candidates: [], matchType: 'none' };
		}
		if (exact.length > 0 && tokenMatches.length === 0) {
			return { candidates: exact, matchType: 'exact' };
		}
		if (exact.length === 0 && tokenMatches.length > 0) {
			return { candidates: tokenMatches, matchType: 'token' };
		}
		// beides — Vereinigung, Aufrufer löst über Koordinaten auf
		return { candidates: [...exact, ...tokenMatches], matchType: 'token' };
	}

	/**
	 * Liefert die fehlenden Vegetationseinträge aus einem bereits importierten
	 * Bohrpunkt-Sheet — also alles, was beim ursprünglichen Import nicht in die
	 * Vegetationsliste gewandert ist:
	 *   1. die RECHTE Spalte der Zeilen 38–44 (Spalte H = Name, U = Häufigkeit)
	 *   2. Overflow-Einträge aus den Bemerkungs-Zeilen 48–51
	 *      (siehe parseBemerkungenVegetationen)
	 *
	 * Hintergrund: bohrpunktFromExcelSheet() las bis April 2026 nur Spalte A/G
	 * und nahm die Bemerkungen als Prosa — beide Datenquellen waren in den
	 * importierten Bohrpunkten verloren. Diese Funktion ermöglicht das gezielte
	 * Nachtragen ohne den Rest des Punkts anzufassen.
	 */
	bohrpunktVegetationRightFromExcelSheet(sheet: Array<any>): BodenvegetationZeile[] {
		const missing: BodenvegetationZeile[] = [];
		for (let i = 0; i < 7; ++i) {
			const row = sheet[38 + i];
			if (!row || row.__rowNum__ >= 45) break;
			if (row.H) {
				missing.push({
					vegetation: this.matchVegetation(row.H?.toString() || ''),
					haeufigkeit: row.U
				});
			}
		}
		// Bemerkungs-Overflow: Zeilen 48–51 Spalte A
		const bemerkungenLines: string[] = [];
		for (let i = 48; i <= 51; i++) {
			const cell = sheet[i]?.A;
			bemerkungenLines.push(cell !== undefined ? String(cell) : '');
		}
		const fromBemerkungen = this.parseBemerkungenVegetationen(bemerkungenLines);
		missing.push(...fromBemerkungen.vegetationen);
		return missing;
	}
}
